import { ErrorMessage, Field, Form, Formik } from "formik";
import { Bank } from "@phosphor-icons/react";
import { FC, useMemo } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useVerifyBVN from "../hooks/useVerifyBVN";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { leaveSetupTask } from "../utils/setupTaskReturn";

const schema = yup.object().shape({
  bvn_number: yup
    .string()
    .required("BVN is required")
    .length(11, "Must be 11 Digits"),
  bvn_gsi_consent: yup
    .boolean()
    .oneOf([true], "You must give consent to continue"),
});

const VerifyBVN: FC<{}> = () => {
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;
  const { isVerifyingBVNError, verifyBvn, verifyingBVN, verifyingBVNError } =
    useVerifyBVN({
      onSuccess() {
        if (fromSetup) {
          leaveSetupTask(navigate, location.state);
        } else {
          navigate(-1);
        }
        toast.success("BVN Verified Successfully");
        refetch();
      },
      onError() {
        toast.error("An error occurred while verifying BVN");
      },
    });

  const isLoading = useMemo(() => verifyingBVN, [verifyingBVN]);

  const isError = useMemo(() => isVerifyingBVNError, [isVerifyingBVNError]);

  const error = useMemo(() => verifyingBVNError, [verifyingBVNError]);

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          bvn_number: "",
          bvn_gsi_consent: false,
        }}
        onSubmit={(values, { resetForm }) => {
          verifyBvn(values, {
            onSuccess() {
              resetForm();
            },
          });
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
              <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
                <Bank size={28} />
              </div>
              <div className="flex items-center flex-col gap-2">
                <div className="font-medium text-xl">Verify BVN</div>
                <p className="text-gray-500 text-center">
                  Kindly provide us with your Bank Verification Number to
                  continue this process.
                </p>
              </div>
              <div className="flex flex-col gap-2 max-w-md w-full">
                <label
                  htmlFor="bvn_number"
                  className="text-sm font-bold text-gray-700"
                >
                  BVN
                </label>
                <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
                  <Field
                    autoComplete="off"
                    type="text"
                    id="bvn_number"
                    name="bvn_number"
                    placeholder="Enter BVN"
                    className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
                  />
                </div>
                <ErrorMessage
                  name="bvn_number"
                  component={motion.div}
                  className="text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2 w-full">
                <label className="flex items-start gap-3 text-sm text-gray-600">
                  <Field
                    type="checkbox"
                    name="bvn_gsi_consent"
                    className="mt-1 accent-deepBlue"
                  />
                  <span>
                    I hereby authorize the use of my BVN for identification and
                    credit assessment and expressly consent that, in the event
                    of default,
                    <strong> Global Standing Instruction (GSI)</strong> may be
                    triggered to debit any of my funded accounts with any bank
                    in Nigeria for the recovery of outstanding loan obligations,
                    in accordance with applicable
                    <strong> CBN regulations</strong>.
                  </span>
                </label>

                <ErrorMessage
                  name="bvn_gsi_consent"
                  component={motion.div}
                  className="text-red-500 text-sm"
                />
              </div>
              {isError && <div className="text-red-500">{error?.message}</div>}
              <div className="flex items-center gap-5 w-full">
                <button
                  type="button"
                  aria-label="Cancel"
                  onClick={() => navigate(-1)}
                  className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !isValid || !dirty}
                  className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {isLoading ? "Verifying" : "Verify"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </motion.div>
  );
};

export default VerifyBVN;
